import { useAiChatActions } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { BiAnswer, biAnswerText } from '@euler/model/bi/answer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { filter } from 'rxjs';

/**
 * A BI widget's tap: sent as `biAnswer` on the next turn, in the words the
 * server stores for it, and pending until that turn's reply ends.
 */
export function useBiTap(
  /** Called once the tap's reply ended. */
  onSettled?: (answer: BiAnswer) => void,
) {
  const { send, busy, tapSettled$ } = useAiChatActions();
  const [pending, setPending] = useState<BiAnswer | null>(null);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  useEffect(() => {
    if (!pending) return;
    const subscription = tapSettled$
      .pipe(filter(tap => tap.kind === 'bi' && tap.answer === pending))
      .subscribe(() => {
        setPending(null);
        settled.current?.(pending);
      });
    return () => subscription.unsubscribe();
  }, [pending, tapSettled$]);
  const tap = useCallback(
    /** `name` is what the tap names, for its words (see `biAnswerText`). */
    (answer: BiAnswer, name?: string): boolean => {
      if (!send(biAnswerText(answer, name), { biAnswer: answer })) return false;
      setPending(answer);
      return true;
    },
    [send],
  );
  return { pending, busy, tap };
}
